import { useAccountStore } from "@/features/account-management";
import { useGetUsersQuery, type TUser } from "@/shared/services";
import { ScrollArea } from "@components/ui/scroll-area";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store";
import { ANALYTICS_QUERY, type VersionType } from "@pages/analytics/types";
import { memo, useEffect, useMemo } from "react";
import { useIntersectionObserver, useWindowSize } from "usehooks-ts";
import { ErrorStateMessage } from "../error-state-message";
import { VersionTableRowSkeleton } from "./version-table-row-skeleton";
import { VersionsTableEmptyState } from "./versions-table-empty-state";
import { VersionsTableHeader } from "./versions-table-header";
import { VersionsTableProLock } from "./versions-table-pro-lock";
import { VersionsTableRow } from "./versions-table-row";

const SKELETON_COUNT = 7;

const parseTime = (timeStr: string): number => {
  if (!timeStr) return 0;

  // Handle colon-separated format: "HH:MM:SS" or "MM:SS"
  const colonMatch = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (colonMatch) {
    const [, hoursOrMinutes, minutesOrSeconds, seconds] = colonMatch;
    // If there are 3 parts, it's HH:MM:SS format
    if (seconds !== undefined) {
      return (
        parseInt(hoursOrMinutes, 10) * 3600 +
        parseInt(minutesOrSeconds, 10) * 60 +
        parseInt(seconds, 10)
      );
    }
    // Otherwise it's MM:SS format
    return parseInt(hoursOrMinutes, 10) * 60 + parseInt(minutesOrSeconds, 10);
  }

  // Handle letter-based format: "5m 30s", "1h 2m", etc.
  const hours = timeStr.match(/(\d+)h/)?.[1] || "0";
  const minutes = timeStr.match(/(\d+)m/)?.[1] || "0";
  const secs = timeStr.match(/(\d+)s/)?.[1] || "0";
  return (
    parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60 + parseInt(secs, 10)
  );
};

const getSortValue = (
  version: VersionType,
  sortBy: string,
  users?: TUser[],
): string | number => {
  switch (sortBy) {
    case "name":
      return version.version_title?.toLowerCase() || "";
    case "readers":
      return version.visitors || 0;
    case "reading-time":
      return parseTime(version.session_avg_time || "");
    case "engaged-users":
      return version.clicked_cta_percentage || 0;
    case "avg-completed":
      return version.avg_completion_level || 0;
    case "created-date":
      return version.created_at ? new Date(version.created_at).getTime() : 0;
    case "recently-read":
      return version.last_event_timestamp
        ? new Date(version.last_event_timestamp).getTime()
        : 0;
    case "owner": {
      const owner = users?.find((user) => user.oktaId === version.creator_id);
      return (owner?.firstName || version.creator_id || "").toLowerCase();
    }
    default:
      return 0;
  }
};

const compareValues = (
  aValue: string | number,
  bValue: string | number,
  sortDirection: string,
): number => {
  // Handle null/undefined values - push them to the end
  if (aValue === null || aValue === undefined) return 1;
  if (bValue === null || bValue === undefined) return -1;

  // Ensure type consistency for comparison
  let comparison: number;
  if (typeof aValue === "number" && typeof bValue === "number") {
    comparison = aValue - bValue;
  } else {
    // Convert both to strings for comparison
    const aStr = String(aValue);
    const bStr = String(bValue);
    comparison = aStr.localeCompare(bStr);
  }

  // Apply sort direction
  return sortDirection === "asc" ? comparison : -comparison;
};

const sortVersions = (
  versions: VersionType[],
  sortBy: string,
  sortDirection: string,
  users?: TUser[],
): VersionType[] => {
  if (!versions.length) return versions;

  return [...versions].sort((a, b) => {
    const aValue = getSortValue(a, sortBy, users);
    const bValue = getSortValue(b, sortBy, users);
    return compareValues(aValue, bValue, sortDirection);
  });
};

const filterVersions = (
  versions: VersionType[],
  searchTerm: string,
): VersionType[] => {
  if (!searchTerm.trim()) {
    return versions;
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  return versions.filter((version) => {
    const versionTitle = (version.version_title || "").toLowerCase();
    const pageTitle = (version.page_title || "").toLowerCase();

    return (
      versionTitle.includes(lowerSearchTerm) ||
      pageTitle.includes(lowerSearchTerm)
    );
  });
};

export const VersionsTableWrapper = memo(
  ({
    sortBy,
    sortDirection,
    searchTerm,
  }: {
    sortBy: string;
    sortDirection: string;
    searchTerm: string;
  }) => {
    const isLocked = useAccountStore(
      (s) => s.planType === "trial" || s.planType === "starter",
    );
    const { data: users } = useGetUsersQuery();
    const showAnalyticsPlaceholder = useAccountStore(
      (s) => s.showAnalyticsPlaceholder,
    );
    const { story, setStory, version, setVersion } = useAnalyticsStoreShallow(
      (s) => ({
        story: s.story,
        version: s.version,
        setStory: s.setStory,
        setVersion: s.setVersion,
      }),
    );
    const { isIntersecting, ref } = useIntersectionObserver({
      threshold: 0.1,
    });

    const hasURLData = useMemo(() => {
      return Boolean(
        (story.id && !story.title) || (version.id && !version.title),
      );
    }, [story.id, story.title, version.id, version.title]);

    const {
      data: response,
      isLoading,
      isError,
    } = useAnalyticsQuery({
      query: [ANALYTICS_QUERY.VERSIONS],
      enabled:
        (!isLocked || showAnalyticsPlaceholder) &&
        (isIntersecting || hasURLData),
    });

    const versions = useMemo(() => {
      const versionsData = response?.[ANALYTICS_QUERY.VERSIONS] as
        | VersionType[]
        | undefined;
      return versionsData || [];
    }, [response]);

    const filteredVersions = useMemo(
      () => filterVersions(versions, searchTerm),
      [versions, searchTerm],
    );

    const sortedVersions = useMemo(
      () => sortVersions(filteredVersions, sortBy, sortDirection, users),
      [filteredVersions, sortBy, sortDirection, users],
    );

    const hasData = sortedVersions.length > 0;

    useEffect(() => {
      if (!versions?.length) return;
      if (story.id && !story.title) {
        const storyData = versions.find((v) => v.story_id === story.id);
        if (storyData)
          setStory({ id: storyData.story_id, title: storyData.page_title });
      }
      if (version.id && !version.title) {
        const versionData = versions.find((v) => v.version_id === version.id);
        if (versionData)
          setVersion({
            id: versionData.version_id,
            title: versionData.version_title,
          });
      }
    }, [story.id, version.id, versions, setStory, setVersion]);

    return (
      <div
        ref={ref}
        className="tw-shadow-2xs tw-rounded-xl tw-outline tw-outline-1 tw-grid tw-outline-blue/5 tw-bg-white tw-overflow-hidden"
      >
        <div className="tw-overflow-x-auto tw-max-w-full">
          <div className="tw-min-w-[900px]">
            {(isLoading || isError || !hasData || (isLocked && !showAnalyticsPlaceholder)) && (
              <VersionsTableHeader />
            )}
            {isLocked && !showAnalyticsPlaceholder ? (
              <VersionsTableProLock />
            ) : (
              <>
                {isError && (
                  <div className="tw-min-h-96">
                    <ErrorStateMessage />
                  </div>
                )}
                {isLoading &&
                  Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <VersionTableRowSkeleton key={index} />
                  ))}
                {!isLoading && !isError && !hasData && (
                  <VersionsTableEmptyState />
                )}
                {!isLoading && !isError && hasData && (
                  <ScrollAreaWrapper>
                    <VersionsTableHeader />
                    {sortedVersions.map((version) => (
                      <VersionsTableRow
                        key={version.version_id}
                        {...version}
                        users={users || []}
                      />
                    ))}
                  </ScrollAreaWrapper>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
);

const ScrollAreaWrapper = ({ children }: { children: React.ReactNode }) => {
  const { width } = useWindowSize();
  const isMobile = width <= 1300;
  if (isMobile) {
    return (
      <div className="tw-overflow-y-auto tw-max-h-[423px]">{children}</div>
    );
  }
  return <ScrollArea maxHeight={423}>{children}</ScrollArea>;
};
